import { useEffect, useLayoutEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { ChevronDown, Check } from "lucide-react";

/**
 * Site-themed dropdown that matches the dark UI exactly. Replaces native
 * <select> usage where the OS-level menu would otherwise show light/system
 * styling.
 *
 * iter32 fixes:
 *   1. Dropdown panel is rendered via React portal at <body> so it
 *      cannot be clipped by parent containers with `overflow:auto`
 *      (the bug the user hit on the document share menu).
 *   2. Scroll-to-close listener IGNORES scrolls whose target is inside
 *      the dropdown panel itself, so flicking through long option
 *      lists (e.g. the timezone picker) doesn't dismiss them.
 *   3. Position recomputed on scroll/resize via getBoundingClientRect.
 *
 * Props:
 * - value: current selected value
 * - onChange(v): selection callback
 * - options: [{ value, label, render? }]
 * - placeholder, className, disabled, "data-testid"
 */
export function ThemedSelect({ value, onChange, options, placeholder = "", className = "", disabled = false, ...props }) {
  const [open, setOpen] = useState(false);
  const wrapRef = useRef(null);
  const panelRef = useRef(null);
  const [pos, setPos] = useState({ left: 0, top: 0, width: 120 });

  // Recompute panel position from the trigger's bounding rect.
  const reposition = () => {
    const el = wrapRef.current;
    if (!el) return;
    const r = el.getBoundingClientRect();
    setPos({ left: r.left, top: r.bottom + 4, width: Math.max(r.width, 120) });
  };

  useLayoutEffect(() => {
    if (!open) return;
    reposition();
    const onDoc = (e) => {
      if (wrapRef.current && wrapRef.current.contains(e.target)) return;
      if (panelRef.current && panelRef.current.contains(e.target)) return;
      setOpen(false);
    };
    const onScroll = (e) => {
      // Ignore scrolls inside the dropdown's own panel.
      if (panelRef.current && e.target instanceof Node && panelRef.current.contains(e.target)) return;
      reposition();
    };
    const onResize = () => reposition();
    window.addEventListener("mousedown", onDoc);
    window.addEventListener("scroll", onScroll, true);
    window.addEventListener("resize", onResize);
    return () => {
      window.removeEventListener("mousedown", onDoc);
      window.removeEventListener("scroll", onScroll, true);
      window.removeEventListener("resize", onResize);
    };
  }, [open]);

  const selected = options.find((o) => o.value === value);
  return (
    <div ref={wrapRef} className={`relative inline-block ${className}`} data-testid={props["data-testid"]}>
      <button
        type="button"
        disabled={disabled}
        onClick={() => setOpen((v) => !v)}
        className="w-full flex items-center justify-between gap-1.5 bg-[#0A0A0A] border border-[#2A2A2A] rounded px-2 py-1 text-[11px] text-[#EDEDED] outline-none hover:border-[#5A90F5] focus:border-[#5A90F5] disabled:opacity-50"
        data-testid={props["data-testid"] ? `${props["data-testid"]}-trigger` : undefined}
      >
        <span className="truncate text-left flex-1" style={selected?.style || undefined}>
          {selected ? (selected.render ? selected.render() : selected.label) : (placeholder || "—")}
        </span>
        <ChevronDown className="w-3 h-3 text-[#A1A1AA] flex-shrink-0" />
      </button>
      {open && createPortal(
        <div
          ref={panelRef}
          className="fixed z-[300] bg-[#141414] border border-[#2A2A2A] rounded-md shadow-2xl py-1 max-h-72 overflow-y-auto"
          style={{ left: pos.left, top: pos.top, minWidth: pos.width }}
          data-testid={props["data-testid"] ? `${props["data-testid"]}-list` : undefined}
          data-themed-select-panel="1"
          onMouseDown={e => e.stopPropagation()}
        >
          {options.map((o) => {
            const active = o.value === value;
            return (
              <button
                key={String(o.value)}
                type="button"
                onClick={() => { onChange(o.value); setOpen(false); }}
                className={`w-full text-left px-2.5 py-1.5 text-[11px] flex items-center gap-2 hover:bg-[#1a1a1a] ${active ? "bg-[#1a1a1a] text-[#EDEDED]" : "text-[#A1A1AA]"}`}
                style={o.style || undefined}
                data-testid={props["data-testid"] ? `${props["data-testid"]}-option-${o.value}` : undefined}
              >
                <span className="flex-1 truncate">{o.render ? o.render() : o.label}</span>
                {active && <Check className="w-3 h-3 text-[#5A90F5] flex-shrink-0" />}
              </button>
            );
          })}
        </div>,
        document.body,
      )}
    </div>
  );
}
